import { requestConfirmation } from "../lib/confirmation";
import BaseBuilderController from "./base_builder_controller"
import { i18n } from "../lib/i18n"

/**
 * Report Builder Controller
 *
 * Extends BaseBuilderController with report-specific functionality:
 * - Report name management
 * - Report-specific targets and values
 *
 * Inherits from BaseBuilderController:
 * - Column management (add, remove, reorder via drag-drop)
 * - Column options (sort, aggregate)
 * - Filter management
 * - Grouping management
 * - Visualization type switching
 * - Chart and stat configuration
 * - Preview loading states
 * - Config sync and save operations
 * - Scope management
 */
export default class extends BaseBuilderController {
  static targets = [
    ...BaseBuilderController.targets,
    "reportName",
    "nameField"
  ]

  static values = {
    ...BaseBuilderController.values,
    reportId: Number
  }

  // ==========================================
  // Abstract method implementations
  // ==========================================

  get entityId() {
    return this.reportIdValue
  }

  get filtersFrameUrl() {
    return `/reports/${this.reportIdValue}/filters`
  }

  get groupHistoryMembersUrl() {
    return `/reports/${this.reportIdValue}/group_history_members`
  }

  get groupingsFrameUrl() {
    return `/reports/${this.reportIdValue}/groupings`
  }

  get previewFrameId() {
    return 'report_preview'
  }

  // ==========================================
  // Data Source Swap
  // ==========================================

  /**
   * Rebind this report to a different data source. Posts via a normal
   * navigation to show?data_source_id=N — the controller rebinds on the server
   * (pruning entries whose field_key doesn't resolve on the new source), then
   * redirects to the clean URL. Unsaved draft state is not preserved across
   * the swap; the warning gives the user a chance to save first.
   */
  async changeDataSource(event) {
    const dataSourceId = event.currentTarget.dataset.dataSourceId
    if (!dataSourceId || !this.hasReportIdValue) return

    if (this.hasUnsavedChanges) {
      const confirmed = await requestConfirmation(i18n.t('report_builder.confirm_swap_data_source'), { intent: 'destructive' })
      if (!confirmed) return
    }

    const url = new URL(`/reports/${this.reportIdValue}`, window.location.origin)
    url.searchParams.set('data_source_id', dataSourceId)
    window.location.href = url.toString()
  }

  // ==========================================
  // Report Name
  // ==========================================

  updateName(event) {
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = event.target.value
    }
    this.updatePreviewTitle(event.target.value)
    this.markDirty()
  }

  // The stat preview's card header shows the report name; keep it in sync as the
  // user types without re-running the report query.
  updatePreviewTitle(value) {
    const title = this.element.querySelector("[data-builder-preview-title]")
    if (title) title.textContent = value
  }

  // ==========================================
  // Report-specific Sync
  // ==========================================

  syncEntitySpecificConfigs() {
    if (this.hasNameFieldTarget && this.hasReportNameTarget) {
      this.nameFieldTarget.value = this.reportNameTarget.value
    }
  }

  // ==========================================
  // Field Picker (override for report-specific URL loading)
  // ==========================================

  openFieldPicker() {
    // Load the field picker content via Turbo Frame if we have reportId
    if (this.hasReportIdValue) {
      const url = new URL(`/reports/${this.reportIdValue}/fields`, window.location.origin)
      // Include draft_config so dynamic (q_<id>) fields resolve against the draft parameters
      url.searchParams.set('draft_config', JSON.stringify(this.buildDraftConfig()))
      const frame = document.getElementById('field_picker_frame')
      if (frame) {
        frame.src = url.toString()
      }
    }
    if (this.hasFieldPickerModalTarget) {
      this.fieldPickerModalTarget.showModal()
    }
  }

  // ==========================================
  // Preview (override for report-specific URL)
  // ==========================================

  refreshPreview() {
    const frame = document.getElementById('report_preview')
    if (frame) {
      if (this.hasPreviewUrlValue && this.previewUrlValue) {
        frame.src = this.previewUrlValue
      } else if (this.hasReportIdValue) {
        frame.src = `/reports/${this.reportIdValue}/preview`
      }
    }
  }
}
